import { Link, useLoaderData, useRevalidator } from 'react-router';
import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import StatusBadge from '@/components/StatusBadge';
import { getTeamStatusDisplay, getProjectTeamStatus } from '@/utils/status';
import { formatDate } from '@/lib/utils';
import supabase from '@/utils/supabase';
import type { ProjectRole } from '@/utils/status';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Project = {
  id: string;
  project_name: string;
  status: string;
  due_date: string | null;
  clients: { company_name: string } | null;
  deliverables: {
    id: string;
    deliverables_team: { team_status: string } | null;
  }[];
};

/**
 * Loads the current user's projects and their membership roles.
 *
 * @returns An object containing the user's projects (with related client and deliverable team data) and a map of project IDs to their roles.
 *
 * @throws 401 if the user is not authenticated.
 * @throws 500 if the projects or memberships query fails.
 */
export async function loader() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  if (!userId) {
    throw new Response('Unauthorised', { status: 401 });
  }

  const { data: projects, error: projectsError } = await supabase
    .from('projects')
    .select(
      'id, project_name, status, due_date, clients(company_name), deliverables(id, deliverables_team(team_status))'
    )
    .order('created_at', { ascending: false });

  if (projectsError || !projects) {
    throw new Response('Failed to load projects', { status: 500 });
  }

  const { data: memberships, error: membershipsError } = await supabase
    .from('project_members')
    .select('project_id, project_role')
    .eq('user_id', userId);

  if (membershipsError) {
    throw new Response('Failed to load project memberships', { status: 500 });
  }

  const membershipMap = Object.fromEntries(
    (memberships ?? []).map((m) => [m.project_id, m.project_role])
  );

  return { projects, membershipMap };
}

/**
 * Displays a table of team projects with their current status, client information, and deadlines.
 *
 * Automatically refreshes project data every 10 seconds to ensure displayed information remains current.
 */
export default function TeamProjects() {
  const { revalidate } = useRevalidator();

  useEffect(() => {
    const interval = setInterval(() => {
      revalidate();
    }, 10_000);
    return () => clearInterval(interval);
  }, [revalidate]);

  const { projects, membershipMap } = useLoaderData() as {
    projects: Project[];
    membershipMap: Record<string, string>;
  };

  return (
    <div className="flex flex-col px-4">
      <Table className="text-base">
        <TableHeader>
          <TableRow>
            <TableHead className="w-40">Status</TableHead>
            <TableHead className="w-auto">Project</TableHead>
            <TableHead className="w-80">Client</TableHead>
            <TableHead className="w-80">Deadline</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((project) => {
            const projectRole = membershipMap[project.id] ?? 'reviewer';
            const teamStatuses = project.deliverables
              .map((d) => d.deliverables_team?.team_status)
              .filter((s): s is string => Boolean(s));
            const statusCode = getProjectTeamStatus(
              teamStatuses,
              projectRole as ProjectRole
            );
            const { label, colour } = getTeamStatusDisplay(
              statusCode,
              projectRole as ProjectRole
            );

            return (
              <TableRow key={project.id}>
                <TableCell>
                  <StatusBadge label={label} colour={colour} />
                </TableCell>
                <TableCell>
                  <Link to={`${project.id}`}>{project.project_name}</Link>
                  <Badge
                    variant={'outline'}
                    className="bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200 ml-4"
                  >
                    {project.deliverables.length}{' '}
                    {project.deliverables.length === 1 ? 'video' : 'videos'}
                  </Badge>
                </TableCell>
                <TableCell>{project.clients?.company_name ?? '-'}</TableCell>
                <TableCell>
                  {project.due_date ? formatDate(project.due_date) : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
